import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useDispatch } from "react-redux";
import { setToken, setUserCredientials } from "../redux/slices/authSlice";
import { FaArrowRight } from "react-icons/fa6";
import { useSelector } from "react-redux";
import { useToast } from "../@/components/ui/use-toast";
import { useVendorLoginMutation } from "../api/api";
import { validateField } from "../lib/utils";
import SideImage from "../components/SideImage";

const Login: React.FC = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const { toast } = useToast();

  const [loginVendor, { isLoading }] = useVendorLoginMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const jwtToken = useSelector((state: any) => state.auth.token);

  useEffect(() => {
    if (jwtToken) {
      navigate("/dashboard");
    }
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter") {
      handleLogin();
    }
  };

  const handleLogin = async () => {
    const emailValidationError = validateField("email", email, {
      required: true,
    });
    const passwordValidationError = validateField("password", password, {
      required: true,
      context: "login",
    });

    setEmailError(emailValidationError);
    setPasswordError(passwordValidationError);

    setTimeout(() => {
      setEmailError(null);
      setPasswordError(null);
    }, 2000);

    if (emailValidationError || passwordValidationError) return;

    try {
      const response = await loginVendor({
        vendorLoginDto: { email, password },
      }).unwrap();

      if (response.token) {
        let payload = {
          email: email,
          password: password,
          isLoginForVendor: true,
        };
        dispatch(setUserCredientials(payload));
        dispatch(setToken(response.token));
        const isFirstLogin = localStorage.getItem("isFirstLogin");
        const isMobile = window.innerWidth <= 768;
        if (isMobile) {
          navigate("/dashboard");
          return;
        }

        if (!isFirstLogin) {
          navigate("/store");
          localStorage.setItem("isFirstLogin", "true");
          return;
        }
        navigate("/dashboard");
      } else {
      }
    } catch (err: any) {
      if (err?.data?.message !== undefined) {
        toast({
          title: "Error!",
          description: `${err?.data?.message}`,
        });
      }
    }
  };

  return (
    <div className="flex min-h-screen select-none flex-col md:flex-row overflow-y-auto">
      <div className="md:w-1/2 w-full  md:min-h-screen max-h-screen xxl:px-60 xl:px-36 lg:px-20 px-8 xxxl:py-10 xl:py-8 py-4 m-auto">
        <img
          src="/Images/LoginPageImages/Auth-Page-Logo.png"
          alt="Auth-Page-Logo"
          className="w-36 mt-10 font-bold mb-3"
        />
        <h2 className="xxl:mt-32 mt-14 font-semibold text-[32px] xs:text-left text-center text-customGrayDark">
          Sign in
        </h2>
        <p className="text-center font-normal mt-1 text-customGrayBase mb-10 text-base xs:text-left">
          Welcome back! Please log in to your account to continue exploring and
          managing your Afrocart experience
        </p>
        <form
          className="xxxl:mt-20 xxl:mt-12 space-y-4"
          autoComplete="new-password"
          onKeyDown={handleKeyDown}
        >
          <div>
            <label
              htmlFor="email"
              className="block text-xs xxl:text-sm font-helveticaMedium text-customGrayBase"
            >
              Enter Email Address<span className="text-red-500">*</span>
            </label>
            <div className="relative mt-1">
              <input
                type="email"
                id="email"
                className={`mt-1 text-customGrayDark block w-full p-3 border ${
                  emailError ? "border-red-500" : "border-gray-300"
                } focus:border-customGreenColor border focus:outline-customGreenColor rounded-lg`}
                value={email}
                autoComplete="off"
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              <div
                className="absolute bg-transparent inset-y-0 right-0 flex items-center px-3"
                style={{ top: "50%", transform: "translateY(-50%)" }}
              >
                <img
                  src="./Images/LoginPageImages/shop.png"
                  className="object-fill text-customGreenColor xxxl:w-6 xxxl:h-6 w-5 h-5"
                />
              </div>
            </div>
            {emailError && (
              <p className="mt-1 text-red-500 text-sm">{emailError}</p>
            )}
          </div>
          <div className="relative">
            <label
              htmlFor="password"
              className="block text-xs xxl:text-sm font-helveticaMedium text-customGrayBase"
            >
              Password<span className="text-red-500">*</span>
            </label>
            <div className="relative mt-1">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                className={`block w-full text-customGrayDark p-3 pr-10 border ${
                  passwordError ? "border-red-500" : "border-gray-300"
                } focus:border-customGreenColor border focus:outline-customGreenColor focus:bg-transparent rounded-lg`}
                value={password}
                autoComplete="off"
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <div
                className="absolute inset-y-0 right-0 flex items-center px-3 cursor-pointer"
                onClick={() => setShowPassword(!showPassword)}
                style={{ top: "50%", transform: "translateY(-50%)" }}
              >
                {showPassword ? (
                  <AiOutlineEyeInvisible className="text-2xl text-customGrayDark" />
                ) : (
                  <AiOutlineEye className="text-2xl text-customGrayDark" />
                )}
              </div>
            </div>
            {passwordError && (
              <p className="mt-1 text-red-500 text-sm">{passwordError}</p>
            )}
          </div>
          <div className="flex xxl:pt-2 items-right text-[1.8vh] justify-start">
            <Link
              to="/forgot-Password"
              className="font-small text-[2vh] text-customBlueColor tracking-tight hover:text-customBlueColor"
            >
              Forgot Password?
            </Link>
          </div>
          <div className="xxxl:pt-2">
            <button
              type="button"
              onClick={handleLogin}
              disabled={isLoading}
              className="disabled:bg-customGreenDisabled flex gap-3 items-center justify-center w-full xxl:py-5 py-3 px-4 border border-transparent rounded-lg shadow-sm xxl:text-sm text-xs font-helveticaMedium text-white bg-customGreenColor hover:bg-customGreenColor focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-150 ease-in-out"
            >
              {isLoading && (
                <div className="border-white h-5 w-5 animate-spin rounded-full border-2 border-t-customGreenColor cursor-not-allowed opacity-50" />
              )}
              {isLoading ? "Logging in..." : "Login"}
              {!isLoading && <FaArrowRight />}
            </button>
            <p className="text-start font-helvetica tracking-normal text-customGrayBase text-sm xxxl:mt-6 xxl:mt-32 mt-10">
              Don't have an account?{" "}
              <Link
                to="/signup"
                className="font-helveticaMedium tracking-wide text-customGreenColor"
              >
                Create free account
              </Link>
            </p>
          </div>
        </form>
      </div>

      <div className="sticky top-0 w-full md:w-1/2 md:flex hidden items-center justify-center bg-white p-6 pt-4 lg:min-h-screen font-hevelticaNeue ">
        <SideImage />
      </div>
    </div>
  );
};

export default Login;
